<template>
  <DashboardContainer>
    <GivarooSpinner v-if="loading" class="mt-24" />
    <template v-else>
      <div class="campaign-data-shell">
        <section class="campaign-data-toolbar">
          <div class="flex flex-wrap items-center gap-2">
            <BaseButton
              v-if="canCreateTasks"
              variant="primary"
              :icon="PlusIcon"
              @click="showCreateModal = true"
            >
              Create New Task
            </BaseButton>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <BaseButton
              v-if="canCreateTasks"
              variant="secondary"
              :icon="FunnelIcon"
              @click="showFilterDrawer = !showFilterDrawer"
            >
              Filters
              <span
                v-if="activeFilterCount"
                class="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white"
              >
                {{ activeFilterCount }}
              </span>
            </BaseButton>
            <p class="text-sm text-gray-500 sm:max-w-xs sm:text-right">
              <span class="font-medium text-gray-700">{{ totalTaskCount }}</span>
              {{ totalTaskCount === 1 ? 'task' : 'tasks' }} total
            </p>
          </div>
        </section>

        <FilterDrawer
          v-if="canCreateTasks"
          :open="showFilterDrawer"
          title="Filter Tasks"
          subtitle="Refine the task list"
          @close="showFilterDrawer = false"
        >
          <div class="filter-fields">
            <FilterMultiselect
              field-label="Assignee"
              v-model="selectedAssigneeFilter"
              :options="availableUsers"
              :multiple="true"
              :close-on-select="false"
              :searchable="true"
              :internal-search="false"
              :loading="isSearchingUsers"
              @search-change="searchUsers"
              label="fullName"
              track-by="id"
              placeholder="All assignees"
              selectLabel="Apply"
              deselectLabel="Remove"
              clearable
              @input="handleFilterChange"
            >
              <template #noResult>
                <span class="text-sm text-gray-500">No users found</span>
              </template>
            </FilterMultiselect>
            <div class="filter-field">
              <label class="filter-field-label">Home Office</label>
              <div
                v-if="!homeOfficesReady"
                class="mt-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500"
              >
                Loading...
              </div>
              <HomeOfficeSelect
                v-else
                v-model="selectedHomeOfficeFilter"
                :multiple="true"
                placeholder="All Home Offices"
                :initial-options="homeOfficesList"
                :initial-has-more="homeOfficesHasMore"
              />
            </div>
          </div>
          <template #footer>
            <BaseButton variant="primary" @click="showFilterDrawer = false">Done</BaseButton>
          </template>
        </FilterDrawer>

        <div class="campaign-data-stats">
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Incomplete</p>
            <p class="campaign-data-stat-card__value">{{ incompleteTasks.length }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Completed</p>
            <p class="campaign-data-stat-card__value">{{ completedTasks.length }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Urgent open</p>
            <p class="campaign-data-stat-card__value">{{ urgentOpenCount }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Important open</p>
            <p class="campaign-data-stat-card__value">{{ importantOpenCount }}</p>
          </div>
        </div>

        <nav class="campaign-data-tabs" aria-label="Task status">
          <button
            type="button"
            class="campaign-data-tab"
            :class="{ 'campaign-data-tab--active': activeTab === 'incomplete' }"
            @click="activeTab = 'incomplete'"
          >
            <ClipboardDocumentListIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Incomplete
            <span class="campaign-data-tab-badge">{{ incompleteTasks.length }}</span>
          </button>
          <button
            type="button"
            class="campaign-data-tab"
            :class="{ 'campaign-data-tab--active': activeTab === 'completed' }"
            @click="activeTab = 'completed'"
          >
            <CheckCircleIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Completed
            <span class="campaign-data-tab-badge">{{ completedTasks.length }}</span>
          </button>
        </nav>

        <section class="campaign-data-workspace">
          <Spinner v-if="updatingTable" class="mt-8" />
          <template v-else>
            <div class="space-y-3 md:hidden">
              <article
                v-for="task in activeTasks"
                :key="task.id"
                class="campaign-data-card cursor-pointer hover:bg-gray-50"
                @click="openTaskDetail(task)"
              >
                <div class="campaign-data-card__header">
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-semibold text-gray-900">
                      {{ taskName(task) }}
                    </p>
                    <div class="mt-2 flex flex-wrap gap-1.5">
                      <span :class="statusBadgeClass(task.status)">
                        {{ task.status === 'completed' ? 'Completed' : 'Not Completed' }}
                      </span>
                      <span v-if="isUrgent(task)" class="task-badge task-badge--urgent"
                        >Urgent</span
                      >
                      <span v-if="isImportant(task)" class="task-badge task-badge--important">
                        Important
                      </span>
                    </div>
                  </div>
                  <BaseButton
                    v-if="activeTab === 'incomplete' && canMarkComplete(task)"
                    variant="ghost"
                    size="xs"
                    @click.stop="markTaskComplete(task)"
                  >
                    Mark Complete
                  </BaseButton>
                </div>
                <div class="campaign-data-card__stats">
                  <div class="campaign-data-stat-row">
                    <span class="campaign-data-stat-label">Assignee</span>
                    <span class="campaign-data-stat-value">{{ assigneeName(task) }}</span>
                  </div>
                  <div class="campaign-data-stat-row">
                    <span class="campaign-data-stat-label">Assigned</span>
                    <span class="campaign-data-stat-value">
                      {{ formatDate(task.dateTaskAssigned || task.date_task_assigned) }}
                    </span>
                  </div>
                  <div v-if="task.dueDate || task.due_date" class="campaign-data-stat-row">
                    <span class="campaign-data-stat-label">Due</span>
                    <span class="campaign-data-stat-value">
                      {{ formatDate(task.dueDate || task.due_date) }}
                    </span>
                  </div>
                  <div
                    v-if="
                      activeTab === 'completed' &&
                      (task.dateTaskCompleted || task.date_task_completed)
                    "
                    class="campaign-data-stat-row"
                  >
                    <span class="campaign-data-stat-label">Completed</span>
                    <span class="campaign-data-stat-value">
                      {{ formatDate(task.dateTaskCompleted || task.date_task_completed) }}
                    </span>
                  </div>
                </div>
              </article>
              <p
                v-if="!activeTasks.length"
                class="rounded-xl bg-white p-6 text-center text-sm text-gray-500 ring-1 ring-gray-200"
              >
                {{ activeTab === 'incomplete' ? 'No incomplete tasks' : 'No completed tasks' }}
              </p>
            </div>

            <div class="hidden md:block">
              <BaseTable
                :columns="activeColumns"
                :rows="activeTasks"
                row-key="id"
                clickable-rows
                :loading="updatingTable"
                :empty-text="
                  activeTab === 'incomplete' ? 'No incomplete tasks' : 'No completed tasks'
                "
                @row-click="openTaskDetail"
              >
                <template #cell-name="{ row }">
                  <span class="font-medium text-gray-900">{{ taskName(row) }}</span>
                </template>
                <template #cell-status="{ row }">
                  <span :class="statusBadgeClass(row.status)">
                    {{ row.status === 'completed' ? 'Completed' : 'Not Completed' }}
                  </span>
                </template>
                <template #cell-assignee="{ row }">{{ assigneeName(row) }}</template>
                <template #cell-urgent="{ row }">
                  <span
                    :class="
                      isUrgent(row)
                        ? 'task-badge task-badge--urgent'
                        : 'task-badge task-badge--neutral'
                    "
                  >
                    {{ isUrgent(row) ? 'Yes' : 'No' }}
                  </span>
                </template>
                <template #cell-important="{ row }">
                  <span
                    :class="
                      isImportant(row)
                        ? 'task-badge task-badge--important'
                        : 'task-badge task-badge--neutral'
                    "
                  >
                    {{ isImportant(row) ? 'Yes' : 'No' }}
                  </span>
                </template>
                <template #cell-dateAssigned="{ row }">
                  {{ formatDate(row.dateTaskAssigned || row.date_task_assigned) }}
                </template>
                <template #cell-dueDate="{ row }">
                  {{ row.dueDate || row.due_date ? formatDate(row.dueDate || row.due_date) : '—' }}
                </template>
                <template #cell-dateCompleted="{ row }">
                  {{
                    row.dateTaskCompleted || row.date_task_completed
                      ? formatDate(row.dateTaskCompleted || row.date_task_completed)
                      : '—'
                  }}
                </template>
                <template #cell-actions="{ row }">
                  <BaseButton
                    v-if="canMarkComplete(row)"
                    variant="ghost"
                    size="xs"
                    @click.stop="markTaskComplete(row)"
                  >
                    Mark Complete
                  </BaseButton>
                  <span v-else class="text-gray-400">—</span>
                </template>
              </BaseTable>
            </div>
          </template>
        </section>
      </div>

      <TaskDetailSidebar
        :isOpen="sidebarOpen"
        :task="selectedTask"
        :canMarkComplete="canMarkComplete"
        :canReopen="canReopen"
        :canEdit="canEdit"
        :canDelete="canDelete"
        @close="closeTaskDetail"
        @markComplete="handleMarkComplete"
        @reopen="reopenTask"
        @edit="editTask"
        @delete="deleteTask"
      />

      <Modal
        :modalOpen="showEditModal"
        @closeModal="cancelEdit"
        headerText="Edit Task"
        ctaText=""
        :showCancelButton="false"
        :hideCtaOnSuccess="true"
      >
        <form @submit.prevent="updateTask" class="form-stack text-left">
          <div>
            <label for="edit_task_name" class="input--label--small">
              Task Name <span class="text-red-500">*</span>
            </label>
            <input
              id="edit_task_name"
              v-model="newTask.task_name"
              type="text"
              required
              class="input--text--small mt-1 w-full"
              placeholder="Enter a descriptive task name..."
            />
          </div>

          <div>
            <label for="edit_description" class="input--label--small">Description</label>
            <textarea
              id="edit_description"
              v-model="newTask.description"
              rows="4"
              class="input--text--small mt-1 w-full resize-none"
              placeholder="Provide additional details about this task..."
            />
            <p class="mt-1 text-xs text-gray-500">
              Optional: Add any additional context or requirements
            </p>
          </div>

          <div>
            <label for="edit_assignee" class="input--label--small">
              Assignee <span class="text-red-500">*</span>
            </label>
            <div class="mt-1">
              <Multiselect
                v-model="selectedAssignee"
                :options="availableUsers"
                :searchable="true"
                :internal-search="false"
                :loading="isSearchingUsers"
                @search-change="searchUsers"
                label="fullName"
                track-by="id"
                placeholder="Search and select a user..."
                :class="{ 'multiselect--active': selectedAssignee }"
              >
                <template #noResult>
                  <span class="text-sm text-gray-500">No users found. Try a different search.</span>
                </template>
                <template #noOptions>
                  <span class="text-sm text-gray-500">Type to search for users</span>
                </template>
              </Multiselect>
            </div>
          </div>

          <div>
            <label for="edit_due_date" class="input--label--small">Due Date</label>
            <input
              id="edit_due_date"
              v-model="newTask.due_date"
              type="date"
              class="input--text--small field-sm mt-1"
            />
          </div>

          <div class="form-grid">
            <div>
              <label for="edit_urgent" class="input--label--small">Urgent?</label>
              <select
                id="edit_urgent"
                v-model="newTask.urgent"
                class="input--text--small field-sm mt-1 w-full"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div>
              <label for="edit_important" class="input--label--small">Important</label>
              <select
                id="edit_important"
                v-model="newTask.important"
                class="input--text--small field-sm mt-1 w-full"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          </div>

          <div class="form-actions form-section-divider">
            <BaseButton type="button" variant="secondary" @click="cancelEdit">Cancel</BaseButton>
            <BaseButton
              type="submit"
              variant="primary"
              :loading="updatingTask"
              :disabled="updatingTask || !newTask.task_name || !selectedAssignee"
            >
              {{ updatingTask ? 'Updating...' : 'Update Task' }}
            </BaseButton>
          </div>
        </form>
      </Modal>

      <Modal
        :modalOpen="showCreateModal"
        @closeModal="showCreateModal = false"
        headerText="Create New Task"
        ctaText=""
        :showCancelButton="false"
        :hideCtaOnSuccess="true"
      >
        <form @submit.prevent="createTask" class="form-stack text-left">
          <div>
            <label for="task_name" class="input--label--small">
              Task Name <span class="text-red-500">*</span>
            </label>
            <input
              id="task_name"
              v-model="newTask.task_name"
              type="text"
              required
              class="input--text--small mt-1 w-full"
              placeholder="Enter a descriptive task name..."
            />
          </div>

          <div>
            <label for="description" class="input--label--small">Description</label>
            <textarea
              id="description"
              v-model="newTask.description"
              rows="4"
              class="input--text--small mt-1 w-full resize-none"
              placeholder="Provide additional details about this task..."
            />
            <p class="mt-1 text-xs text-gray-500">
              Optional: Add any additional context or requirements
            </p>
          </div>

          <div>
            <label for="assignee" class="input--label--small">
              Assignee <span class="text-red-500">*</span>
            </label>
            <div class="mt-1">
              <Multiselect
                v-model="selectedAssignee"
                :options="availableUsers"
                :searchable="true"
                :internal-search="false"
                :loading="isSearchingUsers"
                @search-change="searchUsers"
                label="fullName"
                track-by="id"
                placeholder="Search and select a user..."
                :class="{ 'multiselect--active': selectedAssignee }"
              >
                <template #noResult>
                  <span class="text-sm text-gray-500">No users found. Try a different search.</span>
                </template>
                <template #noOptions>
                  <span class="text-sm text-gray-500">Type to search for users</span>
                </template>
              </Multiselect>
            </div>
          </div>

          <div>
            <label for="due_date" class="input--label--small">Due Date</label>
            <input
              id="due_date"
              v-model="newTask.due_date"
              type="date"
              class="input--text--small field-sm mt-1"
            />
          </div>

          <div class="form-grid">
            <div>
              <label for="urgent" class="input--label--small">Urgent?</label>
              <select
                id="urgent"
                v-model="newTask.urgent"
                class="input--text--small field-sm mt-1 w-full"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div>
              <label for="important" class="input--label--small">Important</label>
              <select
                id="important"
                v-model="newTask.important"
                class="input--text--small field-sm mt-1 w-full"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          </div>

          <div class="form-actions form-section-divider">
            <BaseButton type="button" variant="secondary" @click="showCreateModal = false">
              Cancel
            </BaseButton>
            <BaseButton
              type="submit"
              variant="primary"
              :loading="creatingTask"
              :disabled="creatingTask || !newTask.task_name || !selectedAssignee"
            >
              {{ creatingTask ? 'Creating...' : 'Create Task' }}
            </BaseButton>
          </div>
        </form>
      </Modal>
    </template>
  </DashboardContainer>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import { FunnelIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { CheckCircleIcon, ClipboardDocumentListIcon } from '@heroicons/vue/24/outline'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import DashboardContainer from '@/components/DashboardContainer.vue'
import GivarooSpinner from '@/components/GivarooSpinner.vue'
import Spinner from '@/components/Spinner.vue'
import Modal from '@/components/Modal.vue'
import TaskDetailSidebar from '@/components/TaskDetailSidebar.vue'
import { TaskApi } from '@/services/tasks'
import { UserApi, fetchHomeOfficesPage } from '@/services/users'
import HomeOfficeSelect from '@/components/HomeOfficeSelect.vue'

const store = useStore()
const toast = useToast()

const loading = ref(true)
const updatingTable = ref(false)
const hasLoadedTasks = ref(false)
const tasks = ref([])
const activeTab = ref('incomplete')
const showFilterDrawer = ref(false)
const showCreateModal = ref(false)
const showEditModal = ref(false)
const creatingTask = ref(false)
const updatingTask = ref(false)
const sidebarOpen = ref(false)
const selectedTask = ref(null)
const editingTask = ref(null)
const newTask = ref({
  task_name: '',
  description: '',
  due_date: null,
  urgent: 'no',
  important: 'no',
})
const selectedAssignee = ref(null)
const availableUsers = ref([])
const isSearchingUsers = ref(false)
const selectedAssigneeFilter = ref([])
const selectedHomeOfficeFilter = ref([])
const homeOfficesList = ref([])
const homeOfficesHasMore = ref(false)
const homeOfficesReady = ref(false)

const user = computed(() => store.getters.user)
const isCanvassingOrgAdmin = computed(() => store.getters.isCanvassingOrgAdmin)
const isCanvassingOrgFieldDirector = computed(() => store.getters.isCanvassingOrgFieldDirector)
const isGivarooStaff = computed(() => store.getters.isGivarooStaff)
const canCreateTasks = computed(
  () => isCanvassingOrgAdmin.value || isCanvassingOrgFieldDirector.value || isGivarooStaff.value,
)

const incompleteTasks = computed(() =>
  tasks.value.filter((task) => task.status === 'not_completed'),
)
const completedTasks = computed(() => tasks.value.filter((task) => task.status === 'completed'))
const totalTaskCount = computed(() => tasks.value.length)
const urgentOpenCount = computed(
  () => incompleteTasks.value.filter((task) => isUrgent(task)).length,
)
const importantOpenCount = computed(
  () => incompleteTasks.value.filter((task) => isImportant(task)).length,
)
const activeTasks = computed(() =>
  activeTab.value === 'incomplete' ? incompleteTasks.value : completedTasks.value,
)

const activeFilterCount = computed(
  () => selectedAssigneeFilter.value.length + selectedHomeOfficeFilter.value.length,
)

const baseTaskColumns = [
  { key: 'name', label: 'Task Name', minWidth: '12rem' },
  { key: 'status', label: 'Status' },
  { key: 'assignee', label: 'Assignee', minWidth: '10rem' },
  { key: 'urgent', label: 'Urgent', align: 'center' },
  { key: 'important', label: 'Important', align: 'center' },
  { key: 'dateAssigned', label: 'Date Assigned' },
  { key: 'dueDate', label: 'Due Date' },
]
const incompleteColumns = [...baseTaskColumns, { key: 'actions', label: 'Actions' }]
const completedColumns = [...baseTaskColumns, { key: 'dateCompleted', label: 'Date Completed' }]
const activeColumns = computed(() =>
  activeTab.value === 'incomplete' ? incompleteColumns : completedColumns,
)

function taskName(task) {
  return task.taskName || task.task_name
}

function assigneeName(task) {
  return task.assignee?.full_name || task.assignee?.fullName || task.assignee?.email || '—'
}

function isUrgent(task) {
  return task.urgent === 'yes' || task.urgent === true
}

function isImportant(task) {
  return task.important === 'yes' || task.important === true
}

function statusBadgeClass(status) {
  return status === 'completed'
    ? 'task-badge task-badge--completed'
    : 'task-badge task-badge--pending'
}

const canMarkComplete = (task) => {
  if (task.assignee.id === user.value?.id) {
    return true
  }
  return canCreateTasks.value
}

const canReopen = (task) => {
  if (task.assigner?.id === user.value?.id) {
    return true
  }
  return canCreateTasks.value
}

const canEdit = (task) => {
  if (task.assigner?.id === user.value?.id) {
    return true
  }
  return canCreateTasks.value
}

const canDelete = (task) => {
  if (task.assigner?.id === user.value?.id) {
    return true
  }
  return canCreateTasks.value
}

const reopenTask = async (task) => {
  try {
    await TaskApi.update({ id: task.id, status: 'not_completed' })
    toast.success('Task reopened')
    await fetchTasks()
    if (sidebarOpen.value && selectedTask.value?.id === task.id) {
      selectedTask.value.status = 'not_completed'
      selectedTask.value.dateTaskCompleted = null
    }
  } catch (error) {
    console.error('Error reopening task:', error)
    toast.error('Failed to reopen task')
  }
}

const deleteTask = async (task) => {
  if (!confirm(`Are you sure you want to delete "${taskName(task)}"?`)) {
    return
  }
  try {
    await TaskApi.remove(task.id)
    toast.success('Task deleted')
    if (sidebarOpen.value && selectedTask.value?.id === task.id) {
      closeTaskDetail()
    }
    await fetchTasks()
  } catch (error) {
    console.error('Error deleting task:', error)
    toast.error('Failed to delete task')
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return '—'
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch (error) {
    console.error('Error formatting date:', error, dateString)
    return '—'
  }
}

const fetchTasks = async ({ initial = false } = {}) => {
  const useFullPageLoader = initial || !hasLoadedTasks.value
  if (useFullPageLoader) {
    loading.value = true
  } else {
    updatingTable.value = true
  }
  try {
    const filters = {}

    if (selectedAssigneeFilter.value && selectedAssigneeFilter.value.length > 0) {
      const assigneeIds = selectedAssigneeFilter.value
        .map((assignee) => assignee.id || assignee)
        .filter((id) => id)
      if (assigneeIds.length > 0) {
        filters.assignee = assigneeIds.join(',')
      }
    }

    if (selectedHomeOfficeFilter.value && selectedHomeOfficeFilter.value.length > 0) {
      const homeOfficeIds = selectedHomeOfficeFilter.value
        .map((office) => office.id || office)
        .filter((id) => id)
      if (homeOfficeIds.length > 0) {
        filters.home_office = homeOfficeIds.join(',')
      }
    }

    let response
    if (Object.keys(filters).length > 0) {
      response = await TaskApi.csc.listWithFilters(filters)
      tasks.value = response.results || (Array.isArray(response) ? response : [])
    } else {
      response = await TaskApi.list()
      tasks.value = response.results || response || []
    }
  } catch (error) {
    console.error('Error fetching tasks:', error)
    toast.error('Failed to load tasks')
  } finally {
    loading.value = false
    updatingTable.value = false
    hasLoadedTasks.value = true
  }
}

const handleFilterChange = () => {
  fetchTasks()
}

const markTaskComplete = async (task) => {
  try {
    await TaskApi.update({ id: task.id, status: 'completed' })
    toast.success('Task marked as completed')
    await fetchTasks()
  } catch (error) {
    console.error('Error marking task complete:', error)
    toast.error('Failed to mark task as completed')
  }
}

const loadAvailableUsers = async () => {
  try {
    const response = await UserApi.csc.retrieveFilterIndexes()
    availableUsers.value = response.fundraisers || []
  } catch (error) {
    console.error('Error loading users:', error)
  }
}

const loadHomeOffices = async () => {
  try {
    const data = await fetchHomeOfficesPage(1)
    homeOfficesList.value = data.results || []
    homeOfficesHasMore.value = !!data.next
  } catch (error) {
    console.error('Error loading home offices:', error)
  } finally {
    homeOfficesReady.value = true
  }
}

const searchUsers = async (query) => {
  if (!query || query.length < 1) {
    await loadAvailableUsers()
    return
  }

  try {
    isSearchingUsers.value = true
    const queryLower = query.toLowerCase()
    const allUsers =
      availableUsers.value.length > 0
        ? availableUsers.value
        : await (async () => {
            const response = await UserApi.csc.retrieveFilterIndexes()
            return response.fundraisers || []
          })()

    availableUsers.value = allUsers.filter((u) => {
      const fullName = (u.fullName || `${u.firstName || ''} ${u.lastName || ''}`).toLowerCase()
      const email = (u.email || '').toLowerCase()
      return fullName.includes(queryLower) || email.includes(queryLower)
    })
  } catch (error) {
    console.error('Error searching users:', error)
    toast.error('Failed to search users')
  } finally {
    isSearchingUsers.value = false
  }
}

const createTask = async () => {
  if (!selectedAssignee.value) {
    toast.error('Please select an assignee')
    return
  }
  try {
    creatingTask.value = true
    const taskData = {
      task_name: newTask.value.task_name,
      description: newTask.value.description || '',
      assignee_id: selectedAssignee.value.id,
      urgent: newTask.value.urgent || 'no',
      important: newTask.value.important || 'no',
    }
    if (newTask.value.due_date) {
      taskData.due_date = newTask.value.due_date
    }
    await TaskApi.create(taskData)
    toast.success('Task created successfully')
    showCreateModal.value = false
    newTask.value = {
      task_name: '',
      description: '',
      due_date: null,
      urgent: 'no',
      important: 'no',
    }
    selectedAssignee.value = null
    await fetchTasks()
  } catch (error) {
    console.error('Error creating task:', error)
    toast.error('Failed to create task')
  } finally {
    creatingTask.value = false
  }
}

const openTaskDetail = (task) => {
  selectedTask.value = task
  sidebarOpen.value = true
}

const closeTaskDetail = () => {
  sidebarOpen.value = false
  selectedTask.value = null
}

const handleMarkComplete = async (task) => {
  await markTaskComplete(task)
  closeTaskDetail()
}

const editTask = (task) => {
  editingTask.value = task
  newTask.value = {
    task_name: task.taskName || task.task_name || '',
    description: task.description || '',
    due_date: task.dueDate || task.due_date || null,
    urgent: task.urgent || 'no',
    important: task.important || 'no',
  }
  selectedAssignee.value = task.assignee
    ? {
        id: task.assignee.id,
        fullName: task.assignee.fullName || task.assignee.full_name || task.assignee.email,
      }
    : null
  closeTaskDetail()
  showEditModal.value = true
}

const updateTask = async () => {
  if (!editingTask.value) {
    return
  }
  if (!selectedAssignee.value) {
    toast.error('Please select an assignee')
    return
  }
  try {
    updatingTask.value = true
    const taskData = {
      task_name: newTask.value.task_name,
      description: newTask.value.description || '',
      assignee_id: selectedAssignee.value.id,
      urgent: newTask.value.urgent || 'no',
      important: newTask.value.important || 'no',
    }
    if (newTask.value.due_date) {
      taskData.due_date = newTask.value.due_date
    } else {
      taskData.due_date = null
    }
    await TaskApi.update({ id: editingTask.value.id, ...taskData })
    toast.success('Task updated successfully')
    showEditModal.value = false
    editingTask.value = null
    newTask.value = {
      task_name: '',
      description: '',
      due_date: null,
      urgent: 'no',
      important: 'no',
    }
    selectedAssignee.value = null
    await fetchTasks()
  } catch (error) {
    console.error('Error updating task:', error)
    toast.error('Failed to update task')
  } finally {
    updatingTask.value = false
  }
}

const cancelEdit = () => {
  showEditModal.value = false
  editingTask.value = null
  newTask.value = { task_name: '', description: '', due_date: null, urgent: 'no', important: 'no' }
  selectedAssignee.value = null
}

watch(
  [selectedAssigneeFilter, selectedHomeOfficeFilter],
  () => {
    handleFilterChange()
  },
  { deep: true },
)

onMounted(async () => {
  await fetchTasks({ initial: true })
  await loadAvailableUsers()
  await loadHomeOffices()
})
</script>
